import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { ComplaintStepHeader, StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { useComplaintValues } from "@/hooks/useComplaintValues";
import { missingRequired } from "@/utils/complaintSteps";
import { useTaskStore } from "@/store/task";
import { askVasFor } from "@/types/task-actions";

const Fields = {
  ONSET: "Onset",
  QUALITIES: "Qualities",
  FREQUENCY: "Frequency",
  CURRENT_SEVERITY: "CurrentSeverity",
  SLEEP: "Sleep",
  WORK: "Work",
  AGGS: "Aggs",
  RELIEF: "Relief",
} as const;

const options = (list: readonly string[]) => list.map((o) => ({ value: o, label: o }));
const ONSET_OPTIONS = options(["Today", "Yesterday", "Weeks ago", "Months ago", "Years ago"]);
/** "What does it feel like?" — dizziness-specific descriptors (modernization request). */
const FEEL_OPTIONS = options(["Spinning (vertigo)", "Lightheaded", "Off-balance", "Faint"]);
/** Aggravating / relieving factors — from the original app-pwa Dizziness.vue. */
const WORSE_OPTIONS = options(["Getting up from seated/lying", "Turning head", "Lying down", "Unsure"]);
const BETTER_OPTIONS = options(["Not moving", "Moving around", "Lying down", "Sitting", "Medication"]);
const FREQUENCY_OPTIONS = options(["Constant", "Comes and goes", "A few times per week", "Less than once per week"]);
const scale = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => String(from + i)).map((n) => ({ value: n, label: n }));
const SEVERITY_OPTIONS = scale(1, 10);
const IMPACT_OPTIONS = scale(0, 10);
/** A stored rating in range, or null (unanswered). Severity 0 was the old default, not an answer. */
const rating = (raw: string | undefined, min: number) => {
  const n = Number((raw ?? "").trim());
  return raw != null && raw.trim() !== "" && Number.isInteger(n) && n >= min && n <= 10 ? String(n) : null;
};
const list = (raw: string | undefined) => (raw ? raw.split(",").filter(Boolean) : []);

/**
 * Dizziness visit. Ported from app-pwa Dizziness.vue, with a "what does it feel like?" question
 * added. Saves to the shared complaint fields (Onset / Qualities / Frequency / CurrentSeverity)
 * so answers reach the visit summary. Continue → outcome assessment.
 */
export function DizzinessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get("complaintId");

  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);
  // Loads by complaint id alone, so a missing patientID can no longer skip the load (and Continue
  // can no longer save a blank form over the patient's answers).
  const { values, status, retry } = useComplaintValues(complaintId);
  // The case history config can turn the 1-10 rating off; it is then neither asked nor saved.
  const askVas = useTaskStore((s) => askVasFor(s.portalConfigMeta));

  const [onset, setOnset] = useState<string | null>(null);
  const [feel, setFeel] = useState<string | null>(null);
  const [worse, setWorse] = useState<string[]>([]);
  const [better, setBetter] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<string | null>(null);
  // Ratings start unanswered (null) and are only saved once the patient picks one.
  const [severity, setSeverity] = useState<string | null>(null);
  const [sleep, setSleep] = useState<string | null>(null);
  const [work, setWork] = useState<string | null>(null);
  // Fields whose last save failed. One field saving must not hide another field's failure.
  const [failedSaves, setFailedSaves] = useState<ReadonlySet<string>>(new Set());
  const saveError = failedSaves.size > 0;
  const [missing, setMissing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Restore previously entered answers.
  useEffect(() => {
    if (status !== "ready" || !values) return;
    for (const v of values) {
      if (v.code === Fields.ONSET) setOnset(v.value || null);
      else if (v.code === Fields.QUALITIES) setFeel(v.value || null);
      else if (v.code === Fields.FREQUENCY) setFrequency(v.value || null);
      else if (v.code === Fields.CURRENT_SEVERITY) setSeverity(rating(v.value, 1));
      else if (v.code === Fields.SLEEP) setSleep(rating(v.value, 0));
      else if (v.code === Fields.WORK) setWork(rating(v.value, 0));
      else if (v.code === Fields.AGGS) setWorse(list(v.value));
      else if (v.code === Fields.RELIEF) setBetter(list(v.value));
    }
  }, [status, values]);

  // Fields the patient changed on this visit (every change goes through persist).
  const touched = useRef(new Set<string>());
  const persist = async (code: string, value: string) => {
    touched.current.add(code);
    try {
      await updateComplaintValue(code, value);
      setFailedSaves((prev) => {
        if (!prev.has(code)) return prev;
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
      return true;
    } catch {
      setFailedSaves((prev) => new Set(prev).add(code));
      return false;
    }
  };

  const goBack = () => navigate("/complaint/primary-complaint");

  const saveAndContinue = async () => {
    if (status !== "ready") return;
    if (missingRequired([{ id: "dizziness-severity", answered: !askVas || severity != null }])) {
      setMissing(true);
      const el = document.getElementById("dizziness-severity");
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      el?.focus({ preventScroll: true });
      return;
    }
    // Re-save every answered field, plus every field the patient changed on this visit, even when
    // now empty (a clear whose immediate save failed must not leave the old answer on the chart).
    // Untouched blanks are never written, so a skipped question never overwrites anything.
    const saves: Promise<boolean>[] = [];
    const resave = (code: string, value: string) => {
      if (value !== "" || touched.current.has(code)) saves.push(persist(code, value));
    };
    resave(Fields.ONSET, onset ?? "");
    resave(Fields.QUALITIES, feel ?? "");
    resave(Fields.FREQUENCY, frequency ?? "");
    if (askVas) resave(Fields.CURRENT_SEVERITY, severity ?? "");
    resave(Fields.SLEEP, sleep ?? "");
    resave(Fields.WORK, work ?? "");
    resave(Fields.AGGS, worse.join(","));
    resave(Fields.RELIEF, better.join(","));
    setIsSaving(true);
    const results = await Promise.all(saves);
    setIsSaving(false);
    if (results.some((ok) => !ok)) return; // don't advance if any save failed
    const next = new URLSearchParams(searchParams);
    navigate({ pathname: "/complaint/outcome-assesment", search: `?${next.toString()}` });
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4 flex flex-col">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintId} step="dedicated" />

          {status === "loading" && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your answers">
              <span />
              <span />
              <span />
            </div>
          )}

          {status === "error" && <StepLoadError onRetry={retry} />}

          {status === "ready" && (
            <>
              <ChoiceGroup
                id="dizziness-onset"
                legend="When did it start?"
                options={ONSET_OPTIONS}
                value={onset}
                onChange={(v) => {
                  if (v == null) return;
                  setOnset(v);
                  void persist(Fields.ONSET, v);
                }}
              />
              <ChoiceGroup
                id="dizziness-feel"
                clearable
                legend="What does it feel like?"
                options={FEEL_OPTIONS}
                value={feel}
                onChange={(v) => {
                  setFeel(v);
                  void persist(Fields.QUALITIES, v ?? "");
                }}
              />
              <ChoiceGroup
                id="dizziness-worse"
                multiple
                legend="What makes it worse?"
                hint={`(${t.selectAllApply})`}
                options={WORSE_OPTIONS}
                value={worse}
                onChange={(next) => {
                  setWorse(next);
                  void persist(Fields.AGGS, next.join(","));
                }}
              />
              <ChoiceGroup
                id="dizziness-better"
                multiple
                legend="What makes it better?"
                hint={`(${t.selectAllApply})`}
                options={BETTER_OPTIONS}
                value={better}
                onChange={(next) => {
                  setBetter(next);
                  void persist(Fields.RELIEF, next.join(","));
                }}
              />
              <ChoiceGroup
                id="dizziness-frequency"
                legend="How often does it happen?"
                options={FREQUENCY_OPTIONS}
                value={frequency}
                onChange={(v) => {
                  if (v == null) return;
                  setFrequency(v);
                  void persist(Fields.FREQUENCY, v);
                }}
              />
              {askVas && (
                <ChoiceGroup
                  id="dizziness-severity"
                  layout="numbers"
                  required
                  legend="How severe is it?"
                  hint="(1 = minimal, 10 = severe)"
                  options={SEVERITY_OPTIONS}
                  value={severity}
                  error={missing && severity == null ? t.answerToContinue : null}
                  onChange={(v) => {
                    if (v == null) return;
                    setSeverity(v);
                    setMissing(false);
                    void persist(Fields.CURRENT_SEVERITY, v);
                  }}
                />
              )}
              <ChoiceGroup
                id="dizziness-sleep"
                layout="numbers"
                legend="How much does it affect your sleep?"
                hint="(0 = not at all, 10 = severely)"
                options={IMPACT_OPTIONS}
                value={sleep}
                onChange={(v) => {
                  if (v == null) return;
                  setSleep(v);
                  void persist(Fields.SLEEP, v);
                }}
              />
              <ChoiceGroup
                id="dizziness-work"
                layout="numbers"
                legend="How much does it affect your work?"
                hint="(0 = not at all, 10 = severely)"
                options={IMPACT_OPTIONS}
                value={work}
                onChange={(v) => {
                  if (v == null) return;
                  setWork(v);
                  void persist(Fields.WORK, v);
                }}
              />

              {saveError && (
                <p className="choice-group__error" role="alert">
                  We couldn&apos;t save your last answer. Check your connection — your selections are
                  still here; press Continue to try again.
                </p>
              )}
            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue} disabled={isSaving || status !== "ready"}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
